import axios from 'axios';
import authService from '../services/authService';
import { API_CONFIG } from '../config/api.config';

const API_BASE_URL = API_CONFIG.BASE_URL;

// Create axios instance with interceptors
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for authentication
axiosInstance.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    
    if (token) {
      const isExpired = authService.isTokenExpired(token);
      
      if (!isExpired) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only logout on 401 if it's truly an authentication error
    // Don't logout on other errors - let the calling function handle them
    if (error.response?.status === 401 && error.response?.data?.message?.includes('token')) {
      authService.logout();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const refundApi = {
  /**
   * Calculate refund amount before requesting cancellation
   */
  calculateRefund: async (bookingPnr, bookingType) => {
    try {
      const response = await axiosInstance.get("/refunds/calculate", {
        params: {
          bookingPnr,
          bookingType
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error calculating refund:", error);
      throw error.response?.data || error;
    }
  },

  /**
   * Create refund request with bank details
   */
  createRefundRequest: async (refundData) => {
    try {
      const response = await axiosInstance.post("/refunds/request", refundData);
      return response.data;
    } catch (error) {
      console.error("Error creating refund request:", error);
      throw error.response?.data || error;
    }
  },

  /**
   * Get user's all refund requests
   */
  getUserRefunds: async () => {
    try {
      const response = await axiosInstance.get("/refunds/my-refunds");
      return response.data;
    } catch (error) {
      console.error("Error fetching user refunds:", error);
      throw error.response?.data || error;
    }
  },

  /**
   * Get refund details by ID
   */
  getRefundById: async (refundId) => {
    try {
      const response = await axiosInstance.get(`/refunds/${refundId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching refund details:", error);
      throw error.response?.data || error;
    }
  },

  /**
   * Get refund status by booking PNR
   */
  getRefundByBookingPnr: async (bookingPnr) => {
    try {
      const response = await axiosInstance.get(`/refunds/status/${bookingPnr}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching refund status:", error);
      throw error.response?.data || error;
    }
  }
};

export default refundApi;
